import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { MemberTitle } from '@euler/app/tv/components/MemberTitle';
import { LogoutIcon } from '@euler/app/tv/components/dashboard/icons/LogoutIcon';
import { AboutIcon } from '@euler/app/tv/components/settings/icons/AboutIcon';
import { CheckUpdatesIcon } from '@euler/app/tv/components/settings/icons/CheckUpdatesIcon';
import { DarkModeIcon } from '@euler/app/tv/components/settings/icons/DarkModeIcon';
import { LightModeIcon } from '@euler/app/tv/components/settings/icons/LightModeIcon';
import { StoreInfoIcon } from '@euler/app/tv/components/settings/icons/StoreInfoIcon';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import {
  usePresentationTheme,
  usePresentationThemeContext,
} from '@euler/app/tv/theme';
import { Avatar } from '@euler/components/Avatar';
import { Center } from '@euler/components/Center';
import { versionText } from '@euler/components/VersionText';
import { HStack } from '@euler/components/layout/Stack';
import { config } from '@euler/config';
import { isHubBuild } from '@euler/functions/isHub';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { nativeApplicationVersion } from 'expo-application';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, memo, useCallback } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export const SettingsModalContent = memo(
  ({
    onStoreInfoPress,
    onCheckUpdatesPress,
    onAboutPress,
    onLogoutPress,
    onVersionPress,
    onExitPresentationMode,
  }: {
    onStoreInfoPress?: () => void;
    onCheckUpdatesPress?: () => void;
    onAboutPress?: () => void;
    onLogoutPress?: () => void;
    onVersionPress?: () => void;
    onExitPresentationMode?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <>
        <HStack>
          <WiredUserInfoView />
          <WiredAppearanceView />
        </HStack>

        <CellsContainer
          css={`
            margin-top: ${rpx(80)}px;
          `}
          onStoreInfoPress={onStoreInfoPress}
          onCheckUpdatesPress={onCheckUpdatesPress}
          onAboutPress={onAboutPress}
          onLogoutPress={onLogoutPress}
        />

        <Typography.Label
          light
          size={rpx(18)}
          color={theme.settings.versionTextColor}
          lineHeight={rpx(36)}
          css={`
            margin-top: ${rpx(40)}px;
            align-self: center;
            text-align: center;
          `}
        >
          {`Powered by zhichetech.com, © 2019-2023. All rights reserved. `}
        </Typography.Label>

        <TouchableOpacity onPress={onVersionPress}>
          <Typography.Label
            light
            size={rpx(18)}
            color={theme.settings.versionTextColor}
            lineHeight={rpx(24)}
            css={`
              align-self: center;
              text-align: center;
            `}
          >
            {`${nativeApplicationVersion} (${versionText()})${
              config.target === 'tv' ? ' build for TV' : ''
            }`}
          </Typography.Label>
        </TouchableOpacity>

        {!isHubBuild() && (
          <TouchableOpacity
            onPress={onExitPresentationMode}
            css={`
              border-width: 0.5px;
              border-color: ${theme.dashboard.navbar.exitPresentationMode
                .borderColor};
              align-self: center;
              align-items: center;
              justify-content: center;
              border-radius: ${rpx(5)}px;
              padding: 0 ${rpx(16)}px;
              margin-top: ${rpx(20)}px;
            `}
          >
            <Typography.Label
              light
              color={theme.dashboard.navbar.exitPresentationMode.textColor}
              size={rpx(18)}
              lineHeight={rpx(32)}
              css={`
                text-align: center;
              `}
            >
              退出演示模式
            </Typography.Label>
          </TouchableOpacity>
        )}
      </>
    );
  },
);

const WiredUserInfoView = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { user } = useCurrentUserDangerously();
    const role = useCurrentUserRole();
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack justifyContent="flex-start" style={style}>
        <Avatar
          uri={user.avatar}
          name={user.name ?? user.nick ?? user.userName}
          css={`
            width: ${rpx(100)}px;
            height: ${rpx(100)}px;
            border-radius: ${rpx(100) / 2}px;
          `}
          textStyle={{ fontSize: rpx(36) }}
        />
        <View
          css={`
            margin-left: ${rpx(20)}px;
          `}
        >
          <Typography.Label
            bold
            size={rpx(36)}
            lineHeight={rpx(54)}
            color={theme.settings.userInfo.titleColor}
          >
            {user.name ?? user.nick ?? user.userName}
          </Typography.Label>
          <HStack
            justifyContent="flex-start"
            css={`
              margin-top: ${rpx(0)}px;
            `}
          >
            <Center
              css={`
                border-width: 1px;
                border-color: ${theme.settings.userInfo.roleBorderColor};
                padding: 0 ${rpx(8)}px;
                border-radius: ${rpx(5)}px;
                margin-right: ${rpx(10)}px;
              `}
            >
              <MemberTitle
                role={role}
                jobTitle={user.jobTitle}
                css={`
                  font-size: ${rpx(20)}px;
                  color: ${theme.settings.userInfo.roleTextColor};
                `}
              />
            </Center>
            {user.mobile != null && (
              <Typography.Label
                regular
                size={rpx(24)}
                color={theme.settings.userInfo.roleTextColor}
              >
                {maskedMobilePhone(user.mobile)}
              </Typography.Label>
            )}
          </HStack>
        </View>
      </HStack>
    );
  },
);

const WiredAppearanceView = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { colorMode, theme, setPreferredColorMode } =
      usePresentationThemeContext();
    const { rpx } = useResponsiveContext();

    const width = rpx(220);
    const slideWidth = rpx(140);
    const translateX = useSharedValue(
      colorMode === 'dark' ? 0 : width - slideWidth,
    );

    const animatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: translateX.value }],
    }));

    const onPress = useCallback(() => {
      translateX.value = withTiming(
        colorMode === 'dark' ? width - slideWidth : 0,
        { duration: 250, easing: Easing.inOut(Easing.linear) },
        () => {
          runOnJS(setPreferredColorMode)(
            colorMode === 'dark' ? 'light' : 'dark',
          );
        },
      );
    }, [colorMode, setPreferredColorMode, slideWidth, translateX, width]);

    return (
      <TouchableOpacity
        css={`
          width: ${width}px;
          height: ${rpx(68)}px;
          border-radius: ${rpx(68) / 2}px;
          border-width: 1px;
          border-color: ${theme.settings.appearance.backgroundColor};
          overflow: hidden;
        `}
        style={style}
        onPress={onPress}
      >
        <Animated.View
          css={`
            width: ${slideWidth}px;
            flex: 1;
            overflow: hidden;
          `}
          style={animatedStyle}
        >
          <LinearGradient
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            colors={[theme.button.startColor, theme.button.endColor]}
            locations={[0, 1]}
            style={StyleSheet.absoluteFill}
            css={`
              border-radius: ${rpx(68) / 2}px;
            `}
          />
          <HStack
            justifyContent="center"
            css={`
              flex: 1;
            `}
          >
            {colorMode === 'dark' ? (
              <DarkModeIcon
                size={rpx(32)}
                color={theme.settings.appearance.darkModeColor}
              />
            ) : (
              <LightModeIcon
                size={rpx(32)}
                color={theme.settings.appearance.lightModeColor}
              />
            )}
            <Typography.Label
              light
              size={rpx(28)}
              color={theme.button.textColor}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            >
              {colorMode === 'dark' ? '深色' : '浅色'}
            </Typography.Label>
          </HStack>
        </Animated.View>
      </TouchableOpacity>
    );
  },
);

const CellsContainer = memo(
  ({
    style,
    onStoreInfoPress,
    onCheckUpdatesPress,
    onAboutPress,
    onLogoutPress,
  }: {
    style?: StyleProp<ViewStyle>;
    onStoreInfoPress?: () => void;
    onCheckUpdatesPress?: () => void;
    onAboutPress?: () => void;
    onLogoutPress?: () => void;
  }) => {
    const theme = usePresentationTheme();
    return (
      <View
        style={style}
        css={`
          flex-direction: row;
          flex-wrap: wrap;
        `}
      >
        <View
          css={`
            width: 1px;
            background-color: ${theme.settings.cell.borderColor};
            position: absolute;
            left: 50%;
            top: 0;
            bottom: 0;
          `}
        />
        <View
          css={`
            height: 1px;
            background-color: ${theme.settings.cell.borderColor};
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
          `}
        />
        <Cell text="门店介绍" Icon={StoreInfoIcon} onPress={onStoreInfoPress} />
        <Cell
          text="检查更新"
          Icon={CheckUpdatesIcon}
          onPress={onCheckUpdatesPress}
        />
        <Cell text="关于我们" Icon={AboutIcon} onPress={onAboutPress} />
        <Cell text="退出登录" Icon={LogoutIcon} onPress={onLogoutPress} />
      </View>
    );
  },
);

const Cell = memo(
  ({
    Icon,
    text,
    style,
    onPress,
  }: {
    Icon: FC<IconProps>;
    text: string;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          justify-content: center;
          align-items: center;
          width: 50%;
          height: ${rpx(200)}px;
        `}
        style={style}
        onPress={onPress}
      >
        <Icon color={theme.settings.cell.iconColor} size={rpx(72)} />
        <Typography.Label
          regular
          size={rpx(32)}
          lineHeight={rpx(48)}
          color={theme.settings.cell.textColor}
          css={`
            margin-top: ${rpx(13)}px;
          `}
        >
          {text}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);
